/**
 * The Bloodline: what survives the Hearth going out.
 *
 * Every run ends. What it banks is **Legacy** — earned by how deep into the
 * dark the settlement lasted — and Legacy is spent here, on permanent unlocks
 * that every later run of every kingdom starts with. This file is the whole
 * catalogue; the arithmetic that turns a dead run into Legacy and a bought
 * unlock into a starting bonus lives in `src/sim/bloodline.ts`, and where the
 * purchases are *kept* (a browser key of their own, outside any run save) is
 * `src/ui/bloodline.ts`'s business — the simulation never reads storage.
 *
 * ── The two rules the numbers were authored against ─────────────────────────
 *
 * **Small, and permanent.** A step is two to eight percent, never a new
 * mechanic and never a number that would carry a bad player past a wall the
 * game means them to learn. Forty runs of Bloodline should feel like a
 * seasoned family, not a cheat code: the whole tree folded together is worth
 * roughly one good relic offer.
 *
 * **Paid in depth, not in grinding.** Legacy comes from waves survived, and
 * later waves pay more (see `waveLegacy`), so ten runs that die at wave 5
 * are worth less than three that die at 20. The fastest way to buy the tree
 * is to get better at the game, which is the only meta-progression worth
 * shipping.
 *
 * ── Effects, and what an unlock may name ────────────────────────────────────
 *
 * `mods` bags follow the same convention as race and leader passives: the
 * decision whether a key multiplies or adds is made against `defaultMods()`
 * at fold time (`applyModBag`), never here — `src/data` may not import it.
 * Two keys need care because *lower* is better: `siegeResist` and
 * `powerCooldown` both multiply something bad, so a defensive unlock writes
 * a value below 1. `start` is added to the opening stockpile **unscaled** by
 * difficulty — an heirloom is what it is, whichever night it is carried into.
 * `powers` are appended to the Sovereign's opening kit.
 *
 * `requires` must point at an unlock *earlier in this array* — the tree is
 * four straight ladders, resolved in one pass in catalogue order, and a test
 * holds that property so nobody authors a cycle.
 */

import type { ResourceBundle } from '../sim/types.ts';

export interface BloodlineBranchDef {
  id: string;
  name: string;
  flavour: string;
}

export interface BloodlineUnlockDef {
  id: string;
  branch: string;
  name: string;
  /** One sentence of voice. */
  flavour: string;
  /** The exact mechanics, printed verbatim on the screen. */
  rules: string;
  /** Legacy. */
  cost: number;
  /** The unlock below this one on the ladder, if any. */
  requires?: string;
  /** Folded like a passive; resolved against `defaultMods()` at fold time. */
  mods?: Record<string, number>;
  /** Added to the opening stockpile, unscaled by difficulty. */
  start?: Partial<ResourceBundle>;
  /** Power ids appended to the opening kit. */
  powers?: string[];
}

export const BLOODLINE_BRANCHES: BloodlineBranchDef[] = [
  {
    id: 'hearth',
    name: 'The Hearth Remembers',
    flavour: 'What the family carried out of every burning town, and in again to the next.',
  },
  {
    id: 'blood',
    name: 'The Old Blood',
    flavour: 'Soldiering is a trade, and the trade runs in families.',
  },
  {
    id: 'stone',
    name: 'The Stones Hold',
    flavour: 'Every wall the line has ever lost taught the next one where the mortar goes.',
  },
  {
    id: 'fire',
    name: 'The Sovereign’s Fire',
    flavour: 'The Will is not learned. It is inherited, and it compounds.',
  },
];

/**
 * The ladder costs: four branches of five, 565 Legacy a branch, 2,260 the
 * tree. A median death at wave 22 on Ash banks about 36 (see `waveLegacy`),
 * a wave-40 run about 100 — so the tree is thirty to fifty runs of honest
 * play, which is the brief's 25–40 hours.
 */
export const BLOODLINE_UNLOCKS: BloodlineUnlockDef[] = [
  // ── The Hearth Remembers ──────────────────────────────────────────────────
  {
    id: 'hearth_embers',
    branch: 'hearth',
    name: 'The Banked Embers',
    flavour: 'The cart is packed before the bell stops ringing. It always was.',
    rules: 'Every run opens with +120 timber, +80 stone and +100 grain, on any difficulty.',
    cost: 20,
    start: { timber: 120, stone: 80, grain: 100 },
  },
  {
    id: 'hearth_hands',
    branch: 'hearth',
    name: 'Practised Hands',
    flavour: 'Nobody in this family measures twice.',
    rules: 'Construction is 5% faster.',
    cost: 45,
    requires: 'hearth_embers',
    mods: { buildSpeed: 1.05 },
  },
  {
    id: 'hearth_granary',
    branch: 'hearth',
    name: 'The Deep Cellars',
    flavour: 'Grandmother dug. Everyone laughed. Everyone ate.',
    rules: '+150 storage, before the first shed is built.',
    cost: 90,
    requires: 'hearth_hands',
    mods: { storage: 150 },
  },
  {
    id: 'hearth_yield',
    branch: 'hearth',
    name: 'The Long Husbandry',
    flavour: 'The land remembers being kept well, and pays it forward.',
    rules: 'All resource production +5%.',
    cost: 160,
    requires: 'hearth_granary',
    mods: { resourceRate: 1.05 },
  },
  {
    id: 'hearth_gold',
    branch: 'hearth',
    name: 'The Buried Strongbox',
    flavour: 'Every generation adds a coin and tells no one where it lies.',
    rules: 'Every run opens with +250 gold, and production a further +3%.',
    cost: 250,
    requires: 'hearth_yield',
    start: { gold: 250 },
    mods: { resourceRate: 1.03 },
  },

  // ── The Old Blood ─────────────────────────────────────────────────────────
  {
    id: 'blood_iron',
    branch: 'blood',
    name: 'The Armoury Debt',
    flavour: 'The smith owed the family. The smith’s children still do.',
    rules: 'Every run opens with +60 iron, and your soldiers have +3% health.',
    cost: 20,
    start: { iron: 60 },
    mods: { unitHp: 1.03 },
  },
  {
    id: 'blood_drill',
    branch: 'blood',
    name: 'The Drill Yard',
    flavour: 'The commands are the same ones, in the same order, for two hundred years.',
    rules: 'Companies gain veterancy 10% faster.',
    cost: 45,
    requires: 'blood_iron',
    mods: { veterancyRate: 1.1 },
  },
  {
    id: 'blood_edge',
    branch: 'blood',
    name: 'The Whetstone Creed',
    flavour: 'A dull blade is a family scandal.',
    rules: 'Your soldiers deal +4% damage.',
    cost: 90,
    requires: 'blood_drill',
    mods: { unitDamage: 1.04 },
  },
  {
    id: 'blood_standards',
    branch: 'blood',
    name: 'The Old Standards',
    flavour: 'Men do not run under a banner their grandfathers held.',
    rules: 'Company morale never falls below an extra 5% of its floor.',
    cost: 160,
    requires: 'blood_edge',
    mods: { moraleFloor: 0.05 },
  },
  {
    id: 'blood_hale',
    branch: 'blood',
    name: 'The Hale Line',
    flavour: 'This family does not die of small things.',
    rules: 'Your soldiers have a further +5% health.',
    cost: 250,
    requires: 'blood_standards',
    mods: { unitHp: 1.05 },
  },

  // ── The Stones Hold ───────────────────────────────────────────────────────
  {
    id: 'stone_mortar',
    branch: 'stone',
    name: 'The Mortar Recipe',
    flavour: 'Ash, sand, and something the mason will not say in front of the priest.',
    rules: 'Walls have +6% health.',
    cost: 20,
    mods: { wallHp: 1.06 },
  },
  {
    id: 'stone_bolts',
    branch: 'stone',
    name: 'The Fletcher’s Ledger',
    flavour: 'Every bolt that ever missed is written down, with the reason.',
    rules: 'Towers deal +6% damage.',
    cost: 45,
    requires: 'stone_mortar',
    mods: { towerDamage: 1.06 },
  },
  {
    id: 'stone_ramparts',
    branch: 'stone',
    name: 'The Second Course',
    flavour: 'Build it, then build it again on top. That is the whole secret.',
    rules: 'Walls have a further +6% health.',
    cost: 90,
    requires: 'stone_bolts',
    mods: { wallHp: 1.06 },
  },
  {
    id: 'stone_braced',
    branch: 'stone',
    name: 'The Braced Foundations',
    flavour: 'The ram hits the wall. The wall has been hit before. The wall is not impressed.',
    rules: 'Your buildings take 8% less siege damage.',
    cost: 160,
    requires: 'stone_ramparts',
    mods: { siegeResist: 0.92 },
  },
  {
    id: 'stone_last_light',
    branch: 'stone',
    name: 'The Last Light',
    flavour: 'It has gone out before, they say. Somebody always breathes it back once.',
    rules: 'Once each Siege, the Hearth survives a killing blow at 1 health.',
    cost: 250,
    requires: 'stone_braced',
    mods: { hearthSave: 1 },
  },

  // ── The Sovereign's Fire ──────────────────────────────────────────────────
  {
    id: 'fire_kindled',
    branch: 'fire',
    name: 'The Kindled Will',
    flavour: 'It stirs faster in the old families. Ask anyone who married in.',
    rules: 'Will regenerates 6% faster.',
    cost: 20,
    mods: { willRegen: 1.06 },
  },
  {
    id: 'fire_breath',
    branch: 'fire',
    name: 'The Long Breath',
    flavour: 'Between the horns, the Sovereign inhales.',
    rules: '+5 Will whenever a wave ends.',
    cost: 45,
    requires: 'fire_kindled',
    mods: { willOnWaveEnd: 5 },
  },
  {
    id: 'fire_focus',
    branch: 'fire',
    name: 'The Inherited Focus',
    flavour: 'The gesture is taught to children as a game. It is not a game.',
    rules: 'Power cooldowns are 6% shorter.',
    cost: 90,
    requires: 'fire_breath',
    mods: { powerCooldown: 0.94 },
  },
  {
    id: 'fire_hearthlight',
    branch: 'fire',
    name: 'The Hearth-Light',
    flavour: 'The oldest power the family holds, and the gentlest, and the one they kept.',
    rules: 'Every run opens knowing Hearth Light, on top of the usual four.',
    cost: 160,
    requires: 'fire_focus',
    powers: ['hearth_light'],
  },
  {
    id: 'fire_deep',
    branch: 'fire',
    name: 'The Deep Fire',
    flavour: 'What comes down when this family calls comes down harder.',
    rules: 'Powers deal +6% damage and reach 4% further.',
    cost: 250,
    requires: 'fire_hearthlight',
    mods: { powerDamage: 1.06, powerRadius: 1.04 },
  },
];

const BY_ID = new Map<string, BloodlineUnlockDef>();
for (const u of BLOODLINE_UNLOCKS) BY_ID.set(u.id, u);

export function bloodlineUnlock(id: string): BloodlineUnlockDef | undefined {
  return BY_ID.get(id);
}

export function unlocksOfBranch(branch: string): BloodlineUnlockDef[] {
  return BLOODLINE_UNLOCKS.filter((u) => u.branch === branch);
}
